import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps } from "../BaseRenderer";
import { FontAwesomeIcon } from "../../shared/FontAwesome";
import {
    CheckCircleIcon,
    CloseIcon,
    CloudUploadIcon,
    DeleteIcon,
    DescriptionIcon,
    ImageIcon,
    UploadFileIcon,
} from "../../shared/ModernFileUploadIcons";
import Button from "../../shared/Button";
import { QuestionValidationErrorIcon } from "../../shared/ValidationErrorIcons";
import useStyle from "./FileUploadRenderer.styles";
import { getFileSize } from "../../../utils/index";
import { useModernFileUploadStyles } from "../../../theme/fileUploadThemeTokens";
import type { Theme } from "../../../types/Theme";

interface Props extends RendererProps {
    variableName?: string;
    questionElementId?: string;
    fileName?: string;
    fileSize?: number;
    fileType?: string;
    fileMime?: string;
    fileStatus?: string;
    fileBlob?: Blob;
    fileProgress?: number;
    isValid?: boolean;
    isRequired?: boolean;
    onFileOpen?: React.MouseEventHandler;
    onClearFile?: Function;
    onFileSelection?: (file: File) => void;
    browseFilesLabel?: string;
    dragFilesLabel?: string;
    uploadErrorMessage?: string;
    acceptedTypesText?: string;
    maxSizeText?: string;
    removeButtonLabel?: string;
    openButtonLabel?: string;
    statusAnnouncement?: string;
}

type FileUploadState = "empty" | "uploading" | "completed";

function fileUploadState(progress: number): FileUploadState {
    if (progress >= 1) return "completed";
    if (progress > 0) return "uploading";
    return "empty";
}

function formatFileUploadSize(bytes: number): string {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) {
        const kb = Math.round(bytes / 1024);
        if (kb >= 1024) return "1.0MB";
        return `${kb}KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function useImagePreviewUrl(fileBlob?: Blob, fileMime?: string): string | null {
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

    React.useEffect(() => {
        const isImage = fileMime?.split("/").shift() === "image";
        if (isImage && fileBlob) {
            const url = URL.createObjectURL(fileBlob);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
        setPreviewUrl(null);
        return undefined;
    }, [fileBlob, fileMime]);

    return previewUrl;
}

export default function FileUploadRenderer(props: Props) {
    const {
        fileName,
        fileSize,
        fileMime,
        fileBlob,
        fileProgress,
        isValid,
        isRequired,
        onFileOpen,
        onFileSelection,
        variableName,
        questionElementId,
        browseFilesLabel,
        dragFilesLabel: dragFileLabel,
        uploadErrorMessage,
        acceptedTypesText,
        maxSizeText,
        removeButtonLabel,
        openButtonLabel,
        statusAnnouncement,
    } = props;
    const theme = useTheme() as Theme;
    const [isDragOver, setIsDragOver] = React.useState(false);
    const dragCounterRef = React.useRef(0);
    const browseBtnRef = React.useRef<HTMLInputElement>(null);

    const progress = fileProgress ?? 0;
    const uploadState = fileUploadState(progress);
    const isComplete = uploadState === "completed";
    const needsProgressBar = isValid && uploadState !== "completed";
    const isModern = useModernFileUploadStyles(theme);
    const classes = useStyle({
        theme,
        isModern,
        isDragOver,
        isComplete,
        needsProgressBar,
    } as {
        theme: Theme;
        isModern?: boolean;
        isDragOver?: boolean;
        isComplete?: boolean;
        needsProgressBar?: boolean;
    });
    const imagePreviewUrl = useImagePreviewUrl(fileBlob, fileMime);

    const isFileSelected = fileName !== undefined && fileName !== "";
    const hintId = `${variableName}_HINT`;
    const titleId = questionElementId ? `${questionElementId}_TITLE` : undefined;
    const labelDescId = questionElementId ? `${questionElementId}_LABEL` : undefined;
    const dropZoneLabelledBy = [titleId, labelDescId].filter(Boolean).join(" ") || undefined;

    function onFileSelected(file: File) {
        if (file) onFileSelection(file);
    }

    function onChange(event: React.ChangeEvent<HTMLInputElement>) {
        const file = event.target.files[0];
        // Reset the input's value so re-selecting the same file (e.g. after × removal)
        // still triggers onChange. File inputs only fire change when value differs.
        event.target.value = "";
        onFileSelected(file);
    }

    function onDrop(event: React.DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer.files && event.dataTransfer.files[0]) {
            onFileSelected(event.dataTransfer.files[0]);
        }

        setIsDragOver(false);
        dragCounterRef.current = 0;
    }

    function onDragOver(event: React.DragEvent) {
        event.preventDefault();
        event.stopPropagation();
    }

    function onDragEnter(event: React.DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current += 1;
        setIsDragOver(true);
    }

    function onDragExit(event: React.DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current > 0) return;
        setIsDragOver(false);
    }

    function onClearFile() {
        props.onClearFile();
    }

    function onKeyPress(event, func: Function) {
        if (event.key === " " || event.key === "Enter") {
            if (func) {
                func();
            }
            event.preventDefault();
        }
    }

    function getLegacyFilePreview() {
        switch (fileMime.split("/").shift()) {
            case "image":
                if (!fileBlob) return <FontAwesomeIcon icon={["far", "file-image"]} />;
                return imagePreviewUrl ? <img alt="" src={imagePreviewUrl} /> : null;
            case "video":
                return <FontAwesomeIcon icon={["far", "file-video"]} />;
            case "audio":
                return <FontAwesomeIcon icon={["far", "file-audio"]} />;
            case "text":
                return <FontAwesomeIcon icon={["far", "file-alt"]} />;
            case "application":
                switch (fileMime.split("/").pop()) {
                    case "pdf":
                        return <FontAwesomeIcon icon={["far", "file-pdf"]} />;
                    case "x-zipped-compressed":
                        return <FontAwesomeIcon icon={["far", "file-archive"]} />;
                    case "vnd.ms-excel":
                    case "vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                        return <FontAwesomeIcon icon={["far", "file-excel"]} />;
                    case "msword":
                    case "vnd.openxmlformats-officedocument.wordprocessingml.document":
                        return <FontAwesomeIcon icon={["far", "file-word"]} />;
                    case "vnd.ms-powerpoint":
                    case "vnd.openxmlformats-officedocument.presentationml.presentation":
                        return <FontAwesomeIcon icon={["far", "file-powerpoint"]} />;
                    default:
                        return <FontAwesomeIcon icon={["far", "file"]} />;
                }
            default:
                return <FontAwesomeIcon icon={["far", "file"]} />;
        }
    }

    function getModernFileIcon() {
        const isImage = fileMime?.split("/").shift() === "image";
        if (isImage && fileBlob) {
            return imagePreviewUrl ? <img alt="" src={imagePreviewUrl} /> : null;
        }
        if (isImage) {
            return <ImageIcon />;
        }
        if (uploadState === "uploading") {
            return <UploadFileIcon />;
        }
        return <DescriptionIcon />;
    }

    function renderValidationErrorIcon() {
        if (isModern) {
            return (
                <QuestionValidationErrorIcon
                    glyphColor={theme.WhiteColor ?? "#FFFFFF"}
                    className={classnames(
                        classes.fileUploadValidationErrorIcon,
                        classes.fileUploadValidationErrorIconModern,
                    )}
                />
            );
        }
        return (
            <FontAwesomeIcon
                icon={["far", "exclamation-triangle"]}
                className={classes.fileUploadValidationErrorIcon}
                aria-hidden="true"
            />
        );
    }

    function renderValidationError() {
        return (
            <div
                className={classnames(
                    classes.fileUploadValidationError,
                    isModern && classes.fileUploadValidationErrorModern,
                )}
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
            >
                {renderValidationErrorIcon()}
                <span className={isModern ? classes.fileUploadValidationErrorText : undefined}>
                    {uploadErrorMessage}
                </span>
            </div>
        );
    }

    function renderModernStatusLine() {
        const pct = Math.round(progress * 100);
        const totalSize = fileSize ?? 0;

        if (uploadState === "completed") {
            return (
                <div className={classes.fileUploadCompletedStatus} aria-hidden="true">
                    <CheckCircleIcon />
                    <span>{pct}%</span>
                </div>
            );
        }

        if (uploadState === "uploading") {
            const uploadedBytes = Math.round(progress * totalSize);
            return (
                <div className={classes.fileUploadStatusLine} aria-hidden="true">
                    <span>
                        {formatFileUploadSize(uploadedBytes)} of {formatFileUploadSize(totalSize)}
                    </span>
                    <span> • </span>
                    <span className={classes.fileUploadStatusPercent}>{pct}%</span>
                </div>
            );
        }

        return (
            <div className={classes.fileUploadStatusLine} aria-hidden="true">
                <span>{formatFileUploadSize(totalSize)}</span>
                <span> • </span>
                <span className={classes.fileUploadStatusPercent}>{pct}%</span>
            </div>
        );
    }

    function renderLegacyFileRow() {
        return (
            <div className={classes.fileUploadSelectedFile}>
                {isValid && progress < 1 && (
                    <div
                        className={classes.fileUploadProgress}
                        style={{
                            width: `${progress * 100}%`,
                        }}
                        aria-hidden="true"
                    />
                )}
                <div className={classes.fileUploadFileInfo}>
                    <div className={classes.fileUploadFileContainer}>
                        <div
                            className={classes.fileUploadFileContainerInner}
                            onClick={onFileOpen}
                            role="button"
                            tabIndex={-1}
                            aria-label={
                                openButtonLabel ? `${openButtonLabel} ${fileName}` : undefined
                            }
                        >
                            <div className={classes.fileUploadFileIcon} aria-hidden="true">
                                {getLegacyFilePreview()}
                            </div>
                            <div className={classes.fileUploadFileDetails}>
                                <div className={classes.fileUploadFileName}>{fileName}</div>
                                <div className={classes.fileUploadFileSize}>
                                    {getFileSize(fileSize)}
                                </div>
                            </div>
                        </div>
                    </div>
                    {isValid ? (
                        <div className={classes.fileUploadProgressText} aria-hidden="true">
                            {(progress * 100).toFixed(0)}%
                        </div>
                    ) : (
                        renderValidationError()
                    )}
                    <div
                        className={classes.fileUploadDeleteButton}
                        onClick={onClearFile}
                        onKeyPress={(event) => {
                            onKeyPress(event, onClearFile);
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={
                            removeButtonLabel ? `${removeButtonLabel} ${fileName}` : undefined
                        }
                    >
                        <FontAwesomeIcon icon="times-circle" aria-hidden="true" />
                    </div>
                </div>
            </div>
        );
    }

    function renderModernFileRow() {
        return (
            <div className={classes.fileUploadSelectedFile}>
                {needsProgressBar && (
                    <>
                        <div className={classes.fileUploadProgressTrack} aria-hidden="true" />
                        <div
                            className={classes.fileUploadProgress}
                            style={{
                                width: `${progress * 100}%`,
                            }}
                            aria-hidden="true"
                        />
                    </>
                )}
                <div className={classes.fileUploadFileInfo}>
                    <div className={classes.fileUploadFileContainer}>
                        <div
                            className={classes.fileUploadFileContainerInner}
                            onClick={onFileOpen}
                            role="button"
                            tabIndex={-1}
                            aria-label={
                                openButtonLabel ? `${openButtonLabel} ${fileName}` : undefined
                            }
                        >
                            <div className={classes.fileUploadFileIcon} aria-hidden="true">
                                {getModernFileIcon()}
                            </div>
                            <div className={classes.fileUploadFileDetails}>
                                <div className={classes.fileUploadFileName}>{fileName}</div>
                                {isValid ? (
                                    renderModernStatusLine()
                                ) : (
                                    renderValidationError()
                                )}
                            </div>
                        </div>
                    </div>
                    <Button
                        buttonType="FILEUPLOAD_ACTION"
                        className={classnames(
                            classes.fileUploadDeleteButton,
                            classes.fileUploadActionButtonModern,
                        )}
                        customIcon={isComplete ? <DeleteIcon /> : <CloseIcon />}
                        displayText={false}
                        iconPosition="start"
                        size={16}
                        text={
                            removeButtonLabel ? `${removeButtonLabel} ${fileName}` : undefined
                        }
                        onClick={onClearFile}
                    />
                </div>
            </div>
        );
    }

    const hasHintText = Boolean(acceptedTypesText) || Boolean(maxSizeText);

    return (
        <div className="file-upload__question">
            <div
                className={classnames(
                    classes.fileUploadContainer,
                    isDragOver && classes.fileUploadContainerDrag,
                )}
                role="group"
                aria-labelledby={dropZoneLabelledBy}
                aria-describedby={hasHintText ? hintId : undefined}
                onDrop={onDrop}
                onDragEnter={onDragEnter}
                onDragExit={onDragExit}
                onDragLeave={onDragExit}
                onDragOver={onDragOver}
            >
                <div className={classes.fileUploadIcon} aria-hidden="true">
                    {isModern ? (
                        <CloudUploadIcon />
                    ) : (
                        <FontAwesomeIcon icon={["far", "cloud-upload"]} size="2x" />
                    )}
                </div>

                <div className={classes.fileUploadOptionText} aria-hidden="true">
                    {dragFileLabel}
                </div>
                <button
                    type="button"
                    className={classes.fileUploadButton}
                    aria-describedby={hasHintText ? hintId : undefined}
                    onClick={() => browseBtnRef.current?.click()}
                >
                    {browseFilesLabel}
                </button>
                <input
                    id={`${variableName}_INPUT`}
                    type="file"
                    className="form-control"
                    hidden
                    onChange={onChange}
                    ref={browseBtnRef}
                    aria-required={isRequired ? true : undefined}
                    tabIndex={-1}
                />
                {hasHintText && (
                    <div id={hintId} className={classes.fileUploadHint}>
                        {acceptedTypesText}
                        {acceptedTypesText && maxSizeText ? " · " : ""}
                        {maxSizeText}
                    </div>
                )}
            </div>
            {isFileSelected &&
                (isModern ? renderModernFileRow() : renderLegacyFileRow())}
            <span
                className={classes.srOnly}
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                {statusAnnouncement}
            </span>
        </div>
    );
}
